import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import PermissionDenied from "@/components/PermissionDenied";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { useGridPagination } from "@/hooks/useGridPagination";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { listEmployees, syncEmployeesFromApi } from "@/lib/hr/employeesStore";
import {
  closeEmployeeLoan,
  createEmployeeLoan,
  listEmployeeLoans,
  listLoanInstallments,
  loansApiErrorMessage,
  skipLoanInstallment,
  updateEmployeeLoan,
  type EmployeeLoanRecord,
  type LoanStatus,
} from "@/lib/hr/loansStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { format } from "date-fns";
import { Banknote, CalendarIcon, Edit, Filter, LayoutGrid, List, Loader2, Plus, Receipt, Search } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

type Draft = {
  loanAmount: string;
  installmentAmount: string;
  startDate: string;
};

const emptyDraft = (): Draft => ({
  loanAmount: "",
  installmentAmount: "",
  startDate: format(new Date(), "yyyy-MM-dd"),
});

function loanTitle(r: EmployeeLoanRecord): string {
  return `Loan · ${r.loanAmount}`;
}

function statusBadge(status: LoanStatus) {
  if (status === "completed") return <Badge variant="secondary">Completed</Badge>;
  return <Badge>Active</Badge>;
}

function safeNumber(v: string): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function EmployeeLoans() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "employee_loan_can_create");
  const canUpdate = can(user, "employee_loan_can_update");
  const canClose = can(user, "employee_loan_can_close");
  const canViewInstallments = can(user, "employee_loan_can_view_installments");
  const canSkipInstallment = can(user, "employee_loan_can_skip_installment");
  const [isForbidden, setIsForbidden] = useState(false);

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);

  const [employeeId, setEmployeeId] = useState<string>("");
  const [rows, setRows] = useState<EmployeeLoanRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<"all" | LoanStatus>("all");
  const [filterStartDate, setFilterStartDate] = useState<Date | undefined>(undefined);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeLoanRecord | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [closingTarget, setClosingTarget] = useState<EmployeeLoanRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);

  const [installmentsOpen, setInstallmentsOpen] = useState(false);
  const [installmentsLoan, setInstallmentsLoan] = useState<EmployeeLoanRecord | null>(null);
  const [installments, setInstallments] = useState<
    { id: string; salaryId: string | null; period: string; amount: string; deductedOn: string | null; status: string; note: string | null }[]
  >([]);
  const [installmentsLoading, setInstallmentsLoading] = useState(false);

  const [skipOpen, setSkipOpen] = useState(false);
  const [skipLoan, setSkipLoan] = useState<EmployeeLoanRecord | null>(null);
  const [skipMonth, setSkipMonth] = useState(() => format(new Date(), "yyyy-MM"));
  const [skipNote, setSkipNote] = useState("");
  const [skipping, setSkipping] = useState(false);
  const [skipPickerOpen, setSkipPickerOpen] = useState(false);
  const [skipPickerYear, setSkipPickerYear] = useState(() => new Date().getFullYear());

  const monthOptions = useMemo(
    () => [
      { v: 1, label: "Jan" },
      { v: 2, label: "Feb" },
      { v: 3, label: "Mar" },
      { v: 4, label: "Apr" },
      { v: 5, label: "May" },
      { v: 6, label: "Jun" },
      { v: 7, label: "Jul" },
      { v: 8, label: "Aug" },
      { v: 9, label: "Sep" },
      { v: 10, label: "Oct" },
      { v: 11, label: "Nov" },
      { v: 12, label: "Dec" },
    ],
    [],
  );

  const skipMonthLabel = useMemo(() => {
    const m = skipMonth.trim();
    if (!/^\d{4}-\d{2}$/.test(m)) return "Pick month";
    const [yy, mm] = m.split("-").map((x) => Number(x));
    const label = monthOptions.find((x) => x.v === mm)?.label ?? String(mm);
    return `${label} ${yy}`;
  }, [skipMonth, monthOptions]);

  useEffect(() => {
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, []);

  useEffect(() => {
    if (!employeeId && employees.length) {
      setEmployeeId(employees[0]?.id ?? "");
    }
  }, [employees, employeeId]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(PAGE_SIZE_OPTIONS[0]);

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setIsForbidden(false);
    try {
      const res = await listEmployeeLoans({ employeeId, page, perPage: pageSize, status: filterStatus });
      setRows(res.rows);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setRows([]);
        return;
      }
      toast.error(loansApiErrorMessage(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId, page, pageSize, filterStatus]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterStartDate) {
        const want = format(filterStartDate, "yyyy-MM-dd");
        if (r.startDate !== want) return false;
      }
      if (!q) return true;
      const hay = `${r.loanAmount} ${r.installmentAmount} ${r.remainingAmount} ${r.totalInstallments} ${r.startDate} ${r.status}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, filterStartDate]);

  const appliedFiltersCount = (filterStatus !== "all" ? 1 : 0) + (filterStartDate ? 1 : 0);
  const resetFilters = () => {
    setFilterStatus("all");
    setFilterStartDate(undefined);
  };

  const paginationKey = `${employeeId}|${query}|${filterStatus}|${filterStartDate?.getTime() ?? ""}|${pageSize}`;
  const { paginatedItems, totalCount, totalPages, rangeStart, rangeEnd } = useGridPagination(filtered, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    if (!employeeId) {
      toast.error("Select an employee first.");
      return;
    }
    setEditing(null);
    setDraft(emptyDraft());
    setModalOpen(true);
  };

  const openEdit = (r: EmployeeLoanRecord) => {
    setEditing(r);
    setDraft({
      loanAmount: r.loanAmount,
      installmentAmount: r.installmentAmount,
      startDate: r.startDate,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setDraft(emptyDraft());
  };

  const previewInstallments = useMemo(() => {
    const loan = safeNumber(draft.loanAmount);
    const inst = safeNumber(draft.installmentAmount);
    if (!loan || !inst || loan <= 0 || inst <= 0) return null;
    return Math.max(1, Math.ceil(loan / inst));
  }, [draft.loanAmount, draft.installmentAmount]);

  const save = async () => {
    if (!employeeId || saving) return;
    const loan = safeNumber(draft.loanAmount);
    const inst = safeNumber(draft.installmentAmount);
    if (!loan || loan <= 0) return toast.error("Loan amount must be a number > 0.");
    if (!inst || inst <= 0) return toast.error("Installment amount must be a number > 0.");
    if (!draft.startDate.trim()) return toast.error("Start date is required.");

    setSaving(true);
    try {
      if (editing) {
        // Loan amount is fixed once created (edit only installment/start/status from backend),
        // but we allow UI to edit installment and start date here.
        const result = await updateEmployeeLoan({
          employeeId,
          id: editing.id,
          patch: { installmentAmount: draft.installmentAmount, startDate: draft.startDate },
        });
        toast.success(result.message);
      } else {
        const result = await createEmployeeLoan({
          employeeId,
          loanAmount: draft.loanAmount,
          installmentAmount: draft.installmentAmount,
          startDate: draft.startDate,
        });
        toast.success(result.message);
      }
      closeModal();
      setPage(1);
      await load();
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmClose = async () => {
    if (!employeeId || !closingTarget || closing) return;
    setClosing(true);
    try {
      const result = await closeEmployeeLoan({ employeeId, id: closingTarget.id });
      toast.success(result.message);
      setClosingTarget(null);
      setPage(1);
      await load();
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setClosing(false);
    }
  };

  const openInstallments = useCallback(async (loan: EmployeeLoanRecord) => {
    if (!employeeId) return;
    setInstallmentsLoan(loan);
    setInstallmentsOpen(true);
    setInstallmentsLoading(true);
    try {
      const res = await listLoanInstallments({ employeeId, loanId: loan.id, perPage: 50, page: 1 });
      setInstallments(
        res.rows.map((x) => ({
          id: x.id,
          salaryId: x.salaryId,
          period: x.periodYear && x.periodMonth ? `${x.periodYear}-${String(x.periodMonth).padStart(2, "0")}` : "—",
          amount: x.installmentAmount,
          deductedOn: x.deductedOn,
          status: x.status,
          note: x.note ?? null,
        })),
      );
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
      setInstallments([]);
    } finally {
      setInstallmentsLoading(false);
    }
  }, [employeeId]);

  const openSkip = (loan: EmployeeLoanRecord) => {
    setSkipLoan(loan);
    setSkipMonth(format(new Date(), "yyyy-MM"));
    setSkipPickerYear(new Date().getFullYear());
    setSkipNote("");
    setSkipOpen(true);
  };

  const submitSkip = async () => {
    if (!employeeId || !skipLoan || skipping) return;
    const m = skipMonth.trim();
    if (!/^\d{4}-\d{2}$/.test(m)) {
      toast.error("Pick a month (YYYY-MM).");
      return;
    }
    const [yy, mm] = m.split("-").map((x) => Number(x));
    if (!yy || !mm) {
      toast.error("Pick a valid month.");
      return;
    }
    setSkipping(true);
    try {
      const result = await skipLoanInstallment({
        employeeId,
        loanId: skipLoan.id,
        periodYear: yy,
        periodMonth: mm,
        note: skipNote,
      });
      toast.success(result.message);
      setSkipOpen(false);
      // Reload installments dialog if open for same loan
      if (installmentsOpen && installmentsLoan?.id === skipLoan.id) {
        await openInstallments(skipLoan);
      }
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setSkipping(false);
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Employee loans access required"
      message="You don’t have permission to view employee loans. Ask an administrator to grant employee_loan_can_view_any."
    />
  ) : (
    <Layout title="Employee loans">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Employee loans</h1>
            <p className="text-sm text-muted-foreground mt-1">Create loans and auto-deduct installments during salary processing.</p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate} disabled={!employeeId}>
              <Plus className="w-4 h-4" />
              Add loan
            </Button>
          ) : null}
        </div>

        <Dialog
          open={modalOpen}
          onOpenChange={(o) => {
            if (!o) closeModal();
            else setModalOpen(true);
          }}
        >
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit loan" : "New loan"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Loan amount</Label>
                <Input
                  className="glass-input h-10"
                  value={draft.loanAmount}
                  onChange={(e) => setDraft((d) => ({ ...d, loanAmount: e.target.value }))}
                  placeholder="e.g. 50000"
                  inputMode="decimal"
                  disabled={!!editing}
                />
                {editing ? <p className="text-xs text-muted-foreground">Loan amount is fixed after creation.</p> : null}
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Installment per month</Label>
                <Input
                  className="glass-input h-10"
                  value={draft.installmentAmount}
                  onChange={(e) => setDraft((d) => ({ ...d, installmentAmount: e.target.value }))}
                  placeholder="e.g. 5000"
                  inputMode="decimal"
                />
                {previewInstallments ? (
                  <p className="text-xs text-muted-foreground">Estimated total installments: {previewInstallments}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Total installments will be calculated automatically.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Start date <span className="text-destructive">*</span>
                </Label>
                <DatePartsPicker
                  value={draft.startDate}
                  onChange={(iso) => setDraft((x) => ({ ...x, startDate: iso }))}
                  fromYear={1980}
                  toYear={new Date().getFullYear() + 5}
                  placeholder="Select start date"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void save()} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={installmentsOpen} onOpenChange={(o) => (!o ? setInstallmentsOpen(false) : setInstallmentsOpen(true))}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Installments</DialogTitle>
            </DialogHeader>
            {installmentsLoan ? (
              <div className="text-sm text-muted-foreground">
                Loan amount <span className="text-foreground font-semibold">{installmentsLoan.loanAmount}</span> · Remaining{" "}
                <span className="text-foreground font-semibold">{installmentsLoan.remainingAmount}</span>
              </div>
            ) : null}
            {installmentsLoading ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            ) : installments.length === 0 ? (
              <div className="text-sm text-muted-foreground">No installments found yet. They will appear after salary processing.</div>
            ) : (
              <div className="divide-y rounded-xl border border-border/50">
                {installments.map((x) => (
                  <div key={x.id} className="p-3 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-muted border border-border/50 flex items-center justify-center shrink-0">
                      <Receipt className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground">Amount: {x.amount}</div>
                      <div className="text-xs text-muted-foreground">
                        Period: {x.period} · Deducted on: {x.deductedOn ?? "—"} · Salary ID: {x.salaryId ?? "—"}
                      </div>
                      {x.note ? <div className="text-xs text-muted-foreground mt-1 truncate">Note: {x.note}</div> : null}
                    </div>
                    <Badge
                      variant={x.status === "paid" ? "secondary" : x.status === "pending" ? "default" : "outline"}
                    >
                      {x.status === "pending" ? "pending payroll" : x.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={skipOpen} onOpenChange={setSkipOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Skip installment for a month</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-1">
              {skipLoan ? (
                <div className="text-xs text-muted-foreground">
                  Loan <span className="text-foreground font-semibold">{skipLoan.loanAmount}</span> · Remaining{" "}
                  <span className="text-foreground font-semibold">{skipLoan.remainingAmount}</span>
                </div>
              ) : null}
              <div className="space-y-1.5">
                <Label>Month</Label>
                <Popover modal={true} open={skipPickerOpen} onOpenChange={setSkipPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className={cn("glass-input h-10 w-full justify-start text-left font-normal")}>
                      <CalendarIcon className="mr-2 h-4 w-4 opacity-70 shrink-0" />
                      {skipMonthLabel}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[320px] p-4 z-[100]" align="start" sideOffset={6}>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => setSkipPickerYear((y) => y - 1)}
                        aria-label="Previous year"
                      >
                        ‹
                      </Button>
                      <div className="flex-1">
                        <Select value={String(skipPickerYear)} onValueChange={(v) => setSkipPickerYear(Number(v))}>
                          <SelectTrigger className="glass-input h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 7 }).map((_, i) => {
                              const y = new Date().getFullYear() - 3 + i;
                              return (
                                <SelectItem key={y} value={String(y)}>
                                  {y}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => setSkipPickerYear((y) => y + 1)}
                        aria-label="Next year"
                      >
                        ›
                      </Button>
                    </div>

                    <div className="mt-4 grid grid-cols-4 gap-2">
                      {monthOptions.map((m) => {
                        const iso = `${skipPickerYear}-${String(m.v).padStart(2, "0")}`;
                        const active = iso === skipMonth;
                        return (
                          <Button
                            key={m.v}
                            type="button"
                            variant={active ? "premium" : "outline"}
                            className={cn("h-9", !active && "glass-input")}
                            onClick={() => {
                              setSkipMonth(iso);
                              setSkipPickerOpen(false);
                            }}
                          >
                            {m.label}
                          </Button>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label>Note (optional)</Label>
                <Input className="glass-input h-10" value={skipNote} onChange={(e) => setSkipNote(e.target.value)} placeholder="e.g. Employee requested skip" />
              </div>
              <div className="text-xs text-muted-foreground">
                Salary processing for that month will <strong className="text-foreground">not deduct</strong> this loan installment.
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={() => setSkipOpen(false)} disabled={skipping}>
                Cancel
              </Button>
              <Button variant="premium" className="gap-2" onClick={() => void submitSkip()} disabled={skipping}>
                {skipping ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Skip month
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="glass-card p-6 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="space-y-1.5 lg:col-span-1">
              <Label>Employee</Label>
              <Select value={employeeId || "__none__"} onValueChange={(v) => setEmployeeId(v === "__none__" ? "" : v)}>
                <SelectTrigger className="glass-input h-10">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select employee…</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {(e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 lg:col-span-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="glass-input pl-9 h-10"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search amount, status, start date…"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button type="button" variant="glass" className="gap-2" onClick={() => setFiltersOpen((o) => !o)}>
                <Filter className="w-4 h-4" />
                Filters
                {appliedFiltersCount > 0 ? (
                  <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">{appliedFiltersCount}</span>
                ) : null}
              </Button>
              {filtersOpen ? (
                <Button type="button" variant="ghost" onClick={resetFilters}>
                  Reset
                </Button>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <GridPerPageSelect id="loan-page-size" pageSize={pageSize} onPageSizeChange={(n) => setPageSize(n as never)} options={PAGE_SIZE_OPTIONS} />
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {filtersOpen ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-xl border border-border/50 bg-muted/10 p-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as never)}>
                  <SelectTrigger className="glass-input h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Start date</Label>
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className={cn("glass-input h-10 w-full justify-start text-left font-normal", !filterStartDate && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                      {filterStartDate ? format(filterStartDate, "PPP") : "Any date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                    <Calendar mode="single" selected={filterStartDate} onSelect={setFilterStartDate} initialFocus />
                    <div className="flex gap-2 border-t p-2">
                      <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterStartDate(undefined)}>
                        Clear
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : null}

          {!loading && filtered.length === 0 ? <div className="text-sm text-muted-foreground">No loans found.</div> : null}

          {!loading && filtered.length > 0 && viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {paginatedItems.map((r, i) => (
                <motion.div key={r.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <CatalogEntityCard
                    title={loanTitle(r)}
                    primaryMeta={{ label: "Remaining", value: r.remainingAmount, icon: Banknote, iconClassName: "text-emerald-600" }}
                    secondaryMeta={{ label: "Start", value: r.startDate, icon: CalendarIcon, iconClassName: "text-sky-600" }}
                    footer={`Installment: ${r.installmentAmount} · Status: ${r.status} · Updated ${formatCatalogAuditDate(r.updatedAt)}`}
                    onEdit={canUpdate ? () => openEdit(r) : undefined}
                    onDelete={canClose && r.status === "active" ? () => setClosingTarget(r) : undefined}
                  />
                  <div className="mt-2 flex items-center justify-between px-1">
                    {statusBadge(r.status)}
                    {canViewInstallments ? (
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => void openInstallments(r)}>
                        View installments
                      </Button>
                    ) : null}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : null}

          {!loading && filtered.length > 0 && viewMode === "list" ? (
            <div className="space-y-3">
              {paginatedItems.map((r, i) => (
                <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }} className="glass-card p-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="h-14 w-16 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                      <Banknote className="w-7 h-7 text-emerald-500/70" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-foreground truncate">
                        {r.status} · loan {r.loanAmount}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                        remaining {r.remainingAmount} · installment {r.installmentAmount} · start {r.startDate}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      {canViewInstallments ? (
                        <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => void openInstallments(r)}>
                          <Receipt className="w-3 h-3" /> Installments
                        </Button>
                      ) : null}
                      {canSkipInstallment ? (
                        <Button
                          variant="glass"
                          size="sm"
                          className="gap-1.5 h-8 text-xs"
                          disabled={r.status !== "active"}
                          onClick={() => openSkip(r)}
                        >
                          Skip month
                        </Button>
                      ) : null}
                      {canUpdate ? (
                        <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => openEdit(r)}>
                          <Edit className="w-3 h-3" /> Edit
                        </Button>
                      ) : null}
                      {canClose ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:bg-destructive/10 h-8 px-3 text-xs"
                          disabled={r.status !== "active"}
                          onClick={() => setClosingTarget(r)}
                        >
                          Close
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : null}

          {!loading && filtered.length > 0 ? (
            <GridPaginationFooter totalCount={totalCount} rangeStart={rangeStart} rangeEnd={rangeEnd} page={page} totalPages={totalPages} onPageChange={setPage} />
          ) : null}
        </div>

        <ConfirmAlertDialog
          open={!!closingTarget}
          onOpenChange={(o) => !o && setClosingTarget(null)}
          title="Close this loan?"
          description={
            closingTarget ? (
              <>
                Close <strong className="text-foreground font-semibold">{closingTarget.loanAmount}</strong> with remaining{" "}
                <strong className="text-foreground font-semibold">{closingTarget.remainingAmount}</strong>?
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel={closing ? "Closing..." : "Close"}
          cancelLabel="Cancel"
          onConfirm={() => void confirmClose()}
        />
      </div>
    </Layout>
  );
}

